import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { decimalOnly, decimalInputProps, digitsOnly, phoneInputProps, pincodeInputProps } from "@/lib/numberInput";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Analytics } from "@/lib/analyticsShim";

export default function RequirementsForm() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    product: "",
    quantity: "",
    company: "",
    pincode: "",
    email: "",
    mobile: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.quantity || !Number.isFinite(Number(form.quantity)) || Number(form.quantity) <= 0) {
      toast.error("Enter a valid numeric quantity");
      return;
    }
    if (form.pincode && form.pincode.length !== 6) {
      toast.error("Pincode must be 6 digits");
      return;
    }
    if (form.mobile.length !== 10) {
      toast.error("Mobile number must be 10 digits");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      toast.success("Quote request received! Our team will contact you shortly.");
      setForm({ product: "", quantity: "", company: "", pincode: "", email: "", mobile: "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="requirements" className="py-16 md:py-20 px-6 md:px-10 bg-white" data-testid="requirements-section">
      <div className="max-w-7xl mx-auto">
        <div
          className="rounded-2xl border border-[#C8A96A]/30 p-6 md:p-10"
          style={{
            background: "linear-gradient(135deg, #FDFBF7 0%, #F5EEDC 100%)",
          }}
        >
          <div className="flex items-center gap-4 flex-wrap mb-6 md:mb-8">
            <h2 className="font-display font-bold text-2xl md:text-3xl lg:text-4xl text-[#111827] tracking-tight">
              Tell Us Your Requirements
            </h2>
            <span className="text-sm md:text-base text-[#4B5563] font-medium">
              Best Rates · Working Capital · Delivery Anywhere
            </span>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end" data-testid="requirements-form">
            <div className="md:col-span-1">
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Enter Product*</label>
              <select
                required
                value={form.product}
                onChange={(e) => setForm({ ...form, product: e.target.value })}
                className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                data-testid="req-product-select"
              >
                <option value="">Select Product</option>
                {products.map((p) => (
                  <option key={p.slug} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Quantity*</label>
              <input
                required
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: decimalOnly(e.target.value, 3) })}
                {...decimalInputProps}
                placeholder="e.g. 50"
                className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                data-testid="req-quantity-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Company*</label>
              <input
                required
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Enter company"
                className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                data-testid="req-company-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Pincode</label>
              <input
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: digitsOnly(e.target.value, 6) })}
                {...pincodeInputProps}
                placeholder="Enter Pincode"
                className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                data-testid="req-pincode-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Enter Email"
                className="w-full bg-white border border-gray-200 rounded-md px-3 py-2.5 text-sm gold-focus"
                data-testid="req-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Mobile Number*</label>
              <div className="flex">
                <span className="inline-flex items-center px-3 bg-gray-100 border border-gray-200 rounded-l-md text-sm font-medium">+91</span>
                <input
                  required
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: digitsOnly(e.target.value, 10) })}
                  {...phoneInputProps}
                  placeholder="9876543210"
                  className="w-full bg-white border border-gray-200 border-l-0 rounded-r-md px-3 py-2.5 text-sm gold-focus"
                  data-testid="req-mobile-input"
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full rounded-md py-2.5 font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="req-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
